import React, { useEffect, useState } from 'react';

import {
    Footer,
    Header,
    Meta
} from '$partials/global';

import {
    AjaxListingContext,
    AjaxListingNextButton
} from '$partials/base';

import {
    ArticleListingItem,
    EventListingItem
} from '$partials/items';

import { IconSearch } from '$partials/icons';
import { SearchCarouselBlock } from '$partials/modules/blocks';
import { useSingleSelectFilter } from '$hooks/filters.js';
import { useAjaxListing } from '$hooks/ajax-listing';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/search.d.ts').SearchPage}
 */
const SearchPage = (props) => {
    // misc
    const data = props.data;

    // state
    const [articles, setArticles] = useState([]);
    const [events, setEvents] = useState([]);
    const [term, setTerm] = useState(data?.items?.term);

    // hooks
    const typeFilter = useSingleSelectFilter(data?.items?.type);

    const listing = useAjaxListing({
        infinite: true,
        items: data?.items?.edges || [],
        page: data?.items?.pageInfo?.page,
        path: '/api/v1/search',
        totalArticle: data?.items?.totalArticle,
        totalEvent: data?.items?.totalEvent,
        totalItems: data?.items?.total,
        totalPages: data?.items?.pageInfo?.totalPages,
        query: {
            term: term,
            type: typeFilter?.value
        }
    });

    useEffect(() => {
        const a = [];
        const e = [];

        listing?.items?.forEach((itm) => {
            if (itm?.node?.__typename === 'EventDetailPage') {
                e.push(itm);
            } else {
                a.push(itm);
            }
        });

        setArticles(a);
        setEvents(e);
    }, [
        listing?.items,
        setArticles,
        setEvents
    ]);

    // methods
    const handleSubmit = (e) => {
        e.preventDefault();
    };

    // misc
    let bgColour = 'bg-surface-panel-blue';
    switch (typeFilter.value) {
        case 'article':
            bgColour = 'bg-surface-panel-red';
            break;

        case 'event':
            bgColour = 'bg-surface-panel-yellow';
            break;
    }

    // render
    return (
        <>
            <Meta
                domain={props.domain}
                noindex={true}
                title={term ? `Search — ${term}` : 'Search'} />

            <Header {...props?.data?.header} />

            <main id='main-content'>
                <div className='layout-container layout-site-outer-padding pb-8 md:pb-16'>
                    <div className={`col-wrapper-breakout rounded-lg ${bgColour}`}>
                        <div className='py-24'>
                            {/* Title */}
                            <h1 className='sr-only'>
                                You searched for &ldquo;{data?.items?.term}&rdquo;
                            </h1>

                            <div>
                                <form
                                    action='/search'
                                    className='flex flex-wrap align-top justify-between'
                                    onSubmit={handleSubmit}>
                                    {/* Search Input */}
                                    <search className='w-full sm:w-4/12 md:w-3/12 col-padding'>
                                        <div className='relative'>
                                            <input
                                                className='input input--search w-full'
                                                defaultValue={data?.items?.term}
                                                id='frm-search'
                                                maxLength={20}
                                                name='term'
                                                onChange={(e) => setTerm(e.target.value)}
                                                placeholder='Search'
                                                type='search' />

                                            <div className='absolute left-20 top-12'>
                                                <IconSearch />
                                            </div>
                                        </div>
                                    </search>

                                    {/* Type Filter */}
                                    <div className='flex flex-wrap gap-4 col-padding'>
                                        {typeFilter.options.length > 1 && (
                                            <fieldset className='flex flex-wrap gap-8'>
                                                <legend className='w-full type-style-sans-body pb-8'>
                                                    <span className='sr-only'>
                                                        Search type filters
                                                    </span>
                                                </legend>

                                                <div>
                                                    <input
                                                        checked={!typeFilter.value}
                                                        className='sr-only peer'
                                                        id='frm-type-All'
                                                        name='type'
                                                        onChange={(e) => typeFilter.setValue(e.target.value)}
                                                        type='radio'
                                                        value='' />

                                                    <label
                                                        className='button-outcome'
                                                        htmlFor='frm-type-All'>
                                                        <span>All</span>
                                                    </label>
                                                </div>

                                                {typeFilter.options.map((opt, idx) => {
                                                    return (
                                                        <div key={idx}>
                                                            <input
                                                                checked={typeFilter.value === opt.value || false}
                                                                className='sr-only peer'
                                                                id={`frm-type-${opt.value}`}
                                                                name='type'
                                                                onChange={(e) => typeFilter.setValue(e.target.value)}
                                                                type='radio'
                                                                value={opt.value} />

                                                            <label
                                                                className='button-outcome'
                                                                htmlFor={`frm-type-${opt.value}`}>
                                                                <span>{opt.label}</span>
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </fieldset>
                                        )}
                                    </div>
                                </form>
                            </div>

                            {/* Listings */}
                            <div
                                className={listing?.loading ? 'opacity-50' : ''}
                                inert={listing?.loading}>
                                {typeFilter.value ? (
                                    <section
                                        aria-busy={listing?.loading}
                                        aria-label='Search listing'
                                        className='mt-56 col-padding flex flex-col gap-8 md:gap-16'
                                        role='feed'>
                                        <h2 className='type-style-sans-h2-med'>
                                            {typeFilter.value === 'article' ? 'Insights' : 'Events'}
                                        </h2>

                                        <h3 className='type-style-sans-h3'>
                                            {listing?.totalItems} {listing?.totalItems === 1 ? 'Result' : 'Results'}
                                        </h3>

                                        <AjaxListingContext.Provider value={listing}>
                                            <div className={`gap-8 md:gap-16 ${typeFilter.value === 'article' ? ' grid sm:grid-cols-2 md:grid-cols-3' : ' grid sm:grid-cols-2'}`}>
                                                {listing?.items?.map((itm, idx) => {
                                                    const Component = typeFilter.value === 'article' ? ArticleListingItem : EventListingItem;

                                                    return (
                                                        <article
                                                            aria-labelledby={itm.node.title}
                                                            aria-posinset={idx + 1}
                                                            aria-setsize={listing.items.length}
                                                            className=''
                                                            key={itm.node.id + '-' + idx}>
                                                            <Component {...itm.node} />
                                                        </article>
                                                    );
                                                })}
                                            </div>

                                            {(listing?.items?.length > 0) && (
                                                listing?.page !== listing?.totalPages && (
                                                    <AjaxListingNextButton className='button-outcome mt-56 self-start'>
                                                        <span aria-hidden={true}>Load more&hellip;</span>
                                                    </AjaxListingNextButton>
                                                )
                                            )}
                                        </AjaxListingContext.Provider>
                                    </section>
                                ): (
                                    <>
                                        {(listing?.items?.length === 0) && (
                                            <p className='col-padding type-style-sans-body mt-56'>
                                                <span>No results </span>

                                                <span className='sr-only'>
                                                    found
                                                </span>
                                            </p>
                                        )}

                                        {/* Articles Carousel */}
                                        {listing?.totalArticle > 0 && (
                                            <SearchCarouselBlock
                                                ctaLabel={`View All <span class='type-style-single-line-eyebrow'>${listing.totalArticle}</span>`}
                                                ctaLink={() => typeFilter.setValue('article')}
                                                desktopSlidesPerView={3.15}
                                                items={articles}
                                                title='Insights' />
                                        )}

                                        {/* Events Carousel */}
                                        {listing?.totalEvent > 0 && (
                                            <SearchCarouselBlock
                                                ctaLabel={`View All <span class='type-style-single-line-eyebrow'>${listing.totalEvent}</span>`}
                                                ctaLink={() => typeFilter.setValue('event')}
                                                items={events}
                                                title='Events' />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer {...props?.data?.footer} />
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default SearchPage;
